import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("impo_token") || null);
  const [loading, setLoading] = useState(true);

  const persist = useCallback((tok, usr) => {
    if (tok) localStorage.setItem("impo_token", tok);
    else localStorage.removeItem("impo_token");
    if (usr) localStorage.setItem("impo_user", JSON.stringify(usr));
    else localStorage.removeItem("impo_user");
    setToken(tok);
    setUser(usr);
  }, []);

  const setAuthFromToken = (tok, usr) => persist(tok, usr);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persist(data.access_token, data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    persist(data.access_token, data.user);
    return data.user;
  };

  const logout = () => persist(null, null);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("impo_user", JSON.stringify(data));
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      const t = localStorage.getItem("impo_token");
      if (!t) { setLoading(false); return; }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (e) {
        persist(null, null);
      } finally {
        setLoading(false);
      }
    })();
  }, [persist]);

  const value = useMemo(
    () => ({ user, token, login, register, logout, loading, setAuthFromToken, refreshUser }),
    [user, token, loading, persist, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
